import React from "react";
import groupUngroup from "../../assets/icons/icon-center-horizontal.svg";
import alignleft from "../../assets/icons/icon-alignleft.svg";
import aligncenterYaxis from "../../assets/icons/icon-aligncenter.svg";
import alignright from "../../assets/icons/icon-alignright.svg";
import aligncenterXaxis from "../../assets/icons/icon-horizontal.svg";
import alignbottom from "../../assets/icons/icon-alignbottom.svg";
import aligntop from "../../assets/icons/icon-aligntop.svg";
import duplicate from "../../assets/icons/icon-group.svg";
import idelete from "../../assets/icons/icon-idelete.svg";
import { showPopup } from "../../Store/reducers/popUpSlice";
import { PiFlipVerticalFill } from "react-icons/pi";
import { PiFlipHorizontalFill } from "react-icons/pi";
import { BsLayerForward } from "react-icons/bs";
import { BsLayerBackward } from "react-icons/bs";

import { useDispatch } from "react-redux";

const AlignmentControls = ({ fabricCanvas, saveCanvasState }) => {
  const [canMoveForward, setCanMoveForward] = React.useState(false);
  const [canMoveBackward, setCanMoveBackward] = React.useState(false);

  const offSet = 7;
  const dispatch = useDispatch();

  const getViewportData = () => {
    const zoom = fabricCanvas.getZoom();
    const [a, b, c, d, panX, panY] = fabricCanvas.viewportTransform;
    const visibleWidth = fabricCanvas.getWidth() / zoom;
    const visibleHeight = fabricCanvas.getHeight() / zoom;
    return { zoom, panX, panY, visibleWidth, visibleHeight };
  };

  const containsProtectedObject = (obj) => {
    if (obj.protected) return true;

    if (obj._objects?.length) {
      return obj._objects.some(containsProtectedObject);
    }

    return false;
  };

  const deleteActiveObject = () => {
    const activeObject = fabricCanvas.getActiveObject();

    if (
      (activeObject.type === "group" ||
        activeObject.type === "activeSelection") &&
      containsProtectedObject(activeObject)
    ) {
      dispatch(showPopup("This Group Contains a Protected Svg"));
      return false;
    }

    fabricCanvas.beginBatch();

    if (activeObject.type === "activeSelection") {
      activeObject.getObjects().forEach((obj) => {
        fabricCanvas.remove(obj);
      });
    } else {
      fabricCanvas.remove(activeObject);
    }

    fabricCanvas.discardActiveObject();
    fabricCanvas.renderAll();
    fabricCanvas.endBatch();
  };

  const handleDuplicate = () => {
    const activeObj = fabricCanvas.getActiveObject();
    if (!activeObj) {
      console.error("No active object selected.");
      return;
    }

    const offset = 20;

    // Case A: multiple objects are selected (ActiveSelection)

    if (activeObj.type === "activeSelection") {
      const originals = activeObj.getObjects();

      // 1) Deselect immediately so we don't change originals
      fabricCanvas.discardActiveObject();
      fabricCanvas.renderAll();

      const clones = [];
      let doneCount = 0;
      fabricCanvas.__isLoadingSvglayers = true;
      originals.forEach((obj) => {
        obj.clone((cloneObj) => {
          cloneObj.isPremium = obj.isPremium;
          cloneObj.isMainSvg = obj.isMainSvg;
          cloneObj.data = { ...obj.data };
          cloneObj._skipPopup = true;

          if (obj.type === "group" && obj._objects && cloneObj._objects) {
            obj._objects.forEach((originalChild, i) => {
              const cloneChild = cloneObj._objects[i];
              cloneChild.isPremium = originalChild.isPremium;
              cloneChild.isMainSvg = originalChild.isMainSvg;
              cloneChild.data = { ...originalChild.data };
              cloneChild._skipPopup = true;
            });
          }

          const worldMatrix = obj.calcTransformMatrix();
          const params = fabric.util.qrDecompose(worldMatrix);

          cloneObj.set({
            left: params.translateX + offset,
            top: params.translateY + offset,
            scaleX: params.scaleX,
            scaleY: params.scaleY,
            angle: params.angle,
            skewX: params.skewX,
            skewY: params.skewY,
            originX: "center",
            originY: "center",
          });
          cloneObj.setCoords();

          cloneObj.objectCaching = false;
          cloneObj.perPixelTargetFind = true;

          fabricCanvas.add(cloneObj);
          clones.push(cloneObj);

          doneCount++;
          if (doneCount === originals.length) {
            const newSel = new fabric.ActiveSelection(clones, {
              canvas: fabricCanvas,
            });
            fabricCanvas.setActiveObject(newSel);
            const watermark = fabricCanvas
              .getObjects()
              .find((o) => o.data?.isWatermark);
            if (watermark) fabricCanvas.bringToFront(watermark);
            fabricCanvas.__isLoadingSvglayers = false;
            fabricCanvas.renderAll();
            saveCanvasState();
          }
        });
      });
    } else {
      activeObj.clone((cloneObj) => {
        cloneObj.isPremium = activeObj.isPremium;
        cloneObj.isMainSvg = activeObj.isMainSvg;
        cloneObj.data = { ...activeObj.data };
        cloneObj._skipPopup = true;
        cloneObj._isCloned = true;

        if (
          activeObj.type === "group" &&
          activeObj._objects &&
          cloneObj._objects
        ) {
          activeObj._objects.forEach((originalChild, i) => {
            const cloneChild = cloneObj._objects[i];
            cloneChild.isPremium = originalChild.isPremium;
            cloneChild.isMainSvg = originalChild.isMainSvg;
            cloneChild.data = { ...originalChild.data };
            cloneChild._skipPopup = true;
            cloneObj._isCloned = true;
          });
        }

        const worldMatrix = activeObj.calcTransformMatrix();
        const params = fabric.util.qrDecompose(worldMatrix);

        cloneObj.set({
          left: params.translateX + offset,
          top: params.translateY + offset,
          scaleX: params.scaleX,
          scaleY: params.scaleY,
          angle: params.angle,
          skewX: params.skewX,
          skewY: params.skewY,
          originX: "center",
          originY: "center",
        });
        cloneObj.setCoords();

        cloneObj.objectCaching = false;
        cloneObj.perPixelTargetFind = true;

        fabricCanvas.add(cloneObj);
        fabricCanvas.setActiveObject(cloneObj);
        const watermark = fabricCanvas
          .getObjects()
          .find((o) => o.data?.isWatermark);
        if (watermark) fabricCanvas.bringToFront(watermark);
        fabricCanvas.renderAll();
        saveCanvasState();
      });
    }
  };

  const alignLeft = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom } = getViewportData();
    const leftVisible = -panX / zoom;
    let newLeft = leftVisible;

    if (activeObject.originX === "center") {
      newLeft += activeObject.getScaledWidth() / 2 + offSet / zoom;
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignRight = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom, visibleWidth } = getViewportData();
    const rightVisible = -panX / zoom + visibleWidth;
    let newLeft;
    if (activeObject.originX === "center") {
      newLeft =
        rightVisible - activeObject.getScaledWidth() / 2 - offSet / zoom;
    } else {
      newLeft = rightVisible - activeObject.getScaledWidth();
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignCenterX = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom, visibleWidth } = getViewportData();
    const centerVisible = -panX / zoom + visibleWidth / 2;
    let newLeft;
    if (activeObject.originX === "center") {
      newLeft = centerVisible;
    } else {
      newLeft = centerVisible - activeObject.getScaledWidth() / 2;
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignTop = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom } = getViewportData();
    const topVisible = -panY / zoom;
    let newTop = topVisible;
    if (activeObject.originY === "center") {
      newTop += activeObject.getScaledHeight() / 2 + offSet / zoom;
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignBottom = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom, visibleHeight } = getViewportData();
    const bottomVisible = -panY / zoom + visibleHeight;
    let newTop;
    if (activeObject.originY === "center") {
      newTop =
        bottomVisible - activeObject.getScaledHeight() / 2 - offSet / zoom;
    } else {
      newTop = bottomVisible - activeObject.getScaledHeight();
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignCenterY = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom, visibleHeight } = getViewportData();
    const centerVisible = -panY / zoom + visibleHeight / 2;
    let newTop;
    if (activeObject.originY === "center") {
      newTop = centerVisible;
    } else {
      newTop = centerVisible - activeObject.getScaledHeight() / 2;
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const toggleGroup = (event) => {
    const activeObj = fabricCanvas.getActiveObject();
    if (!activeObj) return;

    fabricCanvas._isGrouping = true;

    if (activeObj.type === "group") {
      const pointer = fabricCanvas.getPointer(event);
      let clickedObject;

      activeObj._objects.forEach((obj) => {
        if (obj.containsPoint(pointer)) {
          clickedObject = obj;
        }
        obj._skipPopup = true;
      });

      activeObj.toActiveSelection();
      activeObj.getObjects().forEach((obj) => {
        obj.canvas = fabricCanvas;
        obj.setCoords();
      });

      if (clickedObject) {
        fabricCanvas.setActiveObject(clickedObject);
      }
    } else if (activeObj.type === "activeSelection") {
      const group = activeObj.toGroup();

      group.isPremium = group._objects.some((obj) => obj.isPremium === true);
      group.isMainSvg = group._objects.some((obj) => obj.isMainSvg === true);
      group.data = group.data || {};

      group._objects.forEach((obj) => {
        if (obj.data) {
          group.data = { ...group.data, ...obj.data };
        }
      });

      group.forEachObject((obj) => {
        obj.canvas = fabricCanvas;
        obj.setCoords();
      });
      group.setCoords();

      // fabricCanvas.fire("object:added", { target: group });
    }
    const watermark = fabricCanvas
      .getObjects()
      .find((obj) => obj.data?.isWatermark);
    if (watermark) {
      fabricCanvas.bringToFront(watermark);
    }
    fabricCanvas.renderAll();
    saveCanvasState();
    if (fabricCanvas._checkLayerMovability) {
    fabricCanvas._checkLayerMovability();
  }

    setTimeout(() => {
      fabricCanvas._isGrouping = false;
    }, 0);
  };

  // Flip vertically
  const flipVertical = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (activeObject) {
      activeObject.set("flipY", !activeObject.flipY);
      activeObject.setCoords();
      fabricCanvas.renderAll();
      saveCanvasState();
    }
  };

  // Flip horizontally
  const flipHorizontal = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (activeObject) {
      activeObject.set("flipX", !activeObject.flipX);
      activeObject.setCoords();
      fabricCanvas.renderAll();
      saveCanvasState();
    }
  };

 React.useEffect(() => {
  if (!fabricCanvas) return;

  const checkLayerMovability = () => {
    const allObjects = fabricCanvas.getObjects();
    const activeObject = fabricCanvas.getActiveObject();

    if (!activeObject) {
      setCanMoveForward(false);
      setCanMoveBackward(false);
      return;
    }

    let selectedObjects;

    if (activeObject.type === "activeSelection") {
      selectedObjects = activeObject.getObjects();
    } else if (activeObject.type === "group") {
      selectedObjects = [activeObject]; 
    } else {
      selectedObjects = [activeObject];
    }

    selectedObjects = selectedObjects.filter((obj) => !obj?.data?.isWatermark);

    const unselectedObjects = allObjects.filter(
      (obj) => !obj?.data?.isWatermark && !selectedObjects.includes(obj)
    );

    if (selectedObjects.length === 0 || unselectedObjects.length === 0) {
      setCanMoveForward(false);
      setCanMoveBackward(false);
      return;
    }

    const selectedIndexes = selectedObjects
      .map((obj) => allObjects.indexOf(obj))
      .sort((a, b) => a - b);

    let canMoveForward = false;
    let canMoveBackward = false;

    for (let i = 0; i < selectedIndexes.length; i++) {
      const currentIndex = selectedIndexes[i];

    
      for (let j = currentIndex + 1; j < allObjects.length; j++) {
        const obj = allObjects[j];
        if (!obj?.data?.isWatermark && !selectedObjects.includes(obj)) {
          canMoveForward = true;
          break;
        }
      }

      
      for (let j = currentIndex - 1; j >= 0; j--) {
        const obj = allObjects[j];
        if (!obj?.data?.isWatermark && !selectedObjects.includes(obj)) {
          canMoveBackward = true;
          break;
        }
      }
    }

    setCanMoveForward(canMoveForward);
    setCanMoveBackward(canMoveBackward);
  };

  fabricCanvas.on("selection:created", checkLayerMovability);
  fabricCanvas.on("selection:updated", checkLayerMovability);
  fabricCanvas.on("selection:cleared", checkLayerMovability);
  fabricCanvas.on("object:added", checkLayerMovability);
  fabricCanvas.on("object:removed", checkLayerMovability);

  fabricCanvas._checkLayerMovability = checkLayerMovability;

  return () => {
    fabricCanvas.off("selection:created", checkLayerMovability);
    fabricCanvas.off("selection:updated", checkLayerMovability);
    fabricCanvas.off("selection:cleared", checkLayerMovability);
    fabricCanvas.off("object:added", checkLayerMovability);
    fabricCanvas.off("object:removed", checkLayerMovability);
  };
}, [fabricCanvas]);

const bringForward = () => {
  const activeObject = fabricCanvas.getActiveObject();
  if (!activeObject) return;

  const objects = fabricCanvas.getObjects();

  let selectedObjects;

  if (activeObject.type === "activeSelection") {
    selectedObjects = activeObject.getObjects();
  } else if (activeObject.type === "group") {
    selectedObjects = [activeObject]; // ✅ Group as single unit
  } else {
    selectedObjects = [activeObject];
  }

  selectedObjects = selectedObjects.filter((obj) => !obj.data?.isWatermark);
  if (selectedObjects.length === 0) return;

  const sortedSelected = selectedObjects
    .map((obj) => ({
      obj,
      index: objects.indexOf(obj)
    }))
    .sort((a, b) => b.index - a.index); // reverse for bring forward

  for (const { obj, index } of sortedSelected) {
    if (index < objects.length - 1) {
      const nextObj = objects[index + 1];
      if (!nextObj?.data?.isWatermark && !selectedObjects.includes(nextObj)) {
        objects.splice(index, 1);
        objects.splice(index + 1, 0, obj);
      }
    }
  }

  fabricCanvas._objects = objects;
  fabricCanvas.renderAll();
  fabricCanvas.setActiveObject(activeObject);
  saveCanvasState();
  fabricCanvas._checkLayerMovability?.();
};

const sendBackward = () => {
  const activeObject = fabricCanvas.getActiveObject();
  if (!activeObject) return;

  const objects = fabricCanvas.getObjects();

  let selectedObjects;

  if (activeObject.type === "activeSelection") {
    selectedObjects = activeObject.getObjects();
  } else if (activeObject.type === "group") {
    selectedObjects = [activeObject]; 
  } else {
    selectedObjects = [activeObject];
  }

  selectedObjects = selectedObjects.filter((obj) => !obj.data?.isWatermark);
  if (selectedObjects.length === 0) return;

  const sortedSelected = selectedObjects
    .map((obj) => ({
      obj,
      index: objects.indexOf(obj)
    }))
    .sort((a, b) => a.index - b.index); 

  for (const { obj, index } of sortedSelected) {
    if (index > 0) {
      const prevObj = objects[index - 1];
      if (!prevObj?.data?.isWatermark && !selectedObjects.includes(prevObj)) {
        objects.splice(index, 1);
        objects.splice(index - 1, 0, obj);
      }
    }
  }

  fabricCanvas._objects = objects;
  fabricCanvas.renderAll();
  fabricCanvas.setActiveObject(activeObject);
  saveCanvasState();
  fabricCanvas._checkLayerMovability?.();
};


  return (
    <div className=" flex flex-col gap-2">
      <div className="flex items-center justify-center space-x-3 cursor-pointer select-none outline-none focus:outline-none">
       
        <img
          src={alignleft}
          alt="Align Left Icon"
         className="w-[19px] h-[19px]"
          onClick={alignLeft}
        />
        <img
          src={aligncenterYaxis}
          alt="Align Center Y Icon"
        className="w-[19px] h-[19px]"
          onClick={alignCenterY}
        />
        <img
          src={alignright}
          alt="Align Right Icon"
         className="w-[19px] h-[19px]"
          onClick={alignRight}
        />
        <img
          src={aligncenterXaxis}
          alt="Align Center X Icon"
           className="w-[19px] h-[19px]"
          onClick={alignCenterX}
        />
        <img
          src={alignbottom}
          alt="Align Bottom Icon"
         className="w-[19px] h-[19px]"
          onClick={alignBottom}
        />
        <img
          src={aligntop}
          alt="Align Top Icon"
          className="w-[19px] h-[19px]"
          onClick={alignTop}
        />
         <img
          src={groupUngroup}
          alt="Group/Ungroup Icon"
           className="w-[19px] h-[19px]"
          onClick={toggleGroup}
        />

      
      </div>

      <div className="flex flex-row items-center space-x-3 cursor-pointer select-none outline-none focus:outline-none">
       

        <PiFlipVerticalFill
          className="w-[19px] h-[19px]"
          onClick={flipVertical}
        />
        <PiFlipHorizontalFill
          className="w-[19px] h-[19px]"
          onClick={flipHorizontal}
        />
        <BsLayerBackward
          onClick={canMoveBackward ? sendBackward : null}
          className={`w-[19px] h-[19px] transition-opacity duration-200 ${
            canMoveBackward
              ? "cursor-pointer"
              : "opacity-30 pointer-events-none"
          }`}
        />

        <BsLayerForward
          onClick={canMoveForward ? bringForward : null}
          className={`w-[19px] h-[19px] transition-opacity duration-200 ${
            canMoveForward ? "cursor-pointer" : "opacity-30 pointer-events-none"
          }`}
        />
          <img
          src={duplicate}
          alt="Duplicate Icon"
         className="w-[19px] h-[19px]"
          onClick={handleDuplicate}
        />

        <img
          src={idelete}
          alt="Delete Icon"
         className="w-[19px] h-[19px]"
          onClick={deleteActiveObject}
        />

      </div>
    </div>
  );
};

export default AlignmentControls;
